import React, { useMemo } from "react";
import { compose } from "redux";

import withAlertManagement from "../../../../../hocs/withAlertManagement";
import withNewNotifications from "../../../../../hocs/withNewNotifications";
import withTranslations from "../../../../../hocs/withTranslations";
import withUserData from "../../../../../hocs/withUserData";
import useSidLatestNotifications from "../../../../../hooks/useSidLatestNotifications";
import toAdditionalSettingsCategories from "../../../../../lib/dto/toAdditionalSettingsCategories";
import {
  areSidNotificationSubscriptionsEnabled,
  getEnabledSidNotificationTypes
} from "../../../../../lib/notifications/sidNotificationSubscriptionStatus";
import {
  getStrategyDashboardEmptyStateTranslationKey,
  getStrategyDashboardI18nKey,
  STRATEGY_DASHBOARD_EMPTY_STATE_TRANSLATION_KEY
} from "../../../../../lib/StrategyDashboard/strategyDashboardConfig";
import { useStrategyDashboardConfig } from "../../../../../lib/StrategyDashboard/StrategyDashboardContext";
import { BodyRegular, H5 } from "../../../common/Typography";
import NoPermissionForPlan from "../../../common/NoPermissionForPlan";
import StrategyEnableNotificationsDesktop from "./StrategyEnableNotificationsDesktop";
import StrategyNoNotificationsYetDesktop from "./StrategyNoNotificationsYetDesktop";
import NotificationFactory from "../../../landingPage/pages/Notifications/components/NotificationFactory";
import Loader from "../../../common/Loader";
import StrategyLatestNotificationsSettingsButton from "./StrategyLatestNotificationsSettingsButton";
import StrategyViewAllNotificationsLink from "./StrategyViewAllNotificationsLink";

const StrategyLatestNotifications = ({
  translate,
  locale,
  authToken,
  userData,
  markAsReadAction,
  alertManagementData,
  loading: alertManagementLoading,
  insiderAnalysisCategories,
  analysisCategories,
  transactionStrategies,
  hasPermission
}) => {
  const dashboard = useStrategyDashboardConfig();
  const i18nKey = suffix => getStrategyDashboardI18nKey(dashboard, suffix);
  const subscriptions = alertManagementData?.global;
  const settingsCategories = useMemo(
    () =>
      toAdditionalSettingsCategories({
        settingsCategories: [
          ...insiderAnalysisCategories,
          ...analysisCategories,
          ...transactionStrategies
        ]
      }),
    [insiderAnalysisCategories, analysisCategories, transactionStrategies]
  );
  const enabledNotificationTypes = useMemo(
    () =>
      getEnabledSidNotificationTypes(
        subscriptions,
        settingsCategories,
        dashboard.notificationTypes
      ),
    [dashboard.notificationTypes, subscriptions, settingsCategories]
  );

  const {
    notifications,
    loading: notificationsLoading
  } = useSidLatestNotifications({
    authToken,
    locale,
    userData,
    enabledNotificationTypes
  });

  const enabledNotificationTypesCount = enabledNotificationTypes.length;

  if (!authToken) {
    return null;
  }

  if (
    dashboard.notificationsPermissionSection &&
    !hasPermission(dashboard.notificationsPermissionSection)
  ) {
    return (
      <div className="flex flex-col mt-8 md:rounded md:bg-white md:py-6 md:px-10">
        <H5 tag="h2" className="mb-3 px-4 md:px-0">
          {translate(i18nKey("latest_notifications_heading"))}
        </H5>
        <NoPermissionForPlan
          className="px-4 md:px-0"
          locale={locale}
          permissions={dashboard.notificationsPermissionSection}
        />
      </div>
    );
  }

  const showEmptyStateLoader =
    notifications.length === 0 &&
    (notificationsLoading || alertManagementLoading);

  const renderEmptyNotificationsState = () => {
    if (
      !areSidNotificationSubscriptionsEnabled(
        subscriptions,
        settingsCategories,
        dashboard.notificationTypes
      )
    ) {
      return <StrategyEnableNotificationsDesktop
        notificationTypes={dashboard.notificationTypes}
      />;
    }

    return (
      <StrategyNoNotificationsYetDesktop
        title={translate(
          getStrategyDashboardEmptyStateTranslationKey(
            dashboard,
            STRATEGY_DASHBOARD_EMPTY_STATE_TRANSLATION_KEY.NO_YET_TITLE
          )
        )}
        description={translate(
          getStrategyDashboardEmptyStateTranslationKey(
            dashboard,
            STRATEGY_DASHBOARD_EMPTY_STATE_TRANSLATION_KEY.NO_YET_DESCRIPTION
          )
        )}
      />
    );
  };

  return (
    <div className="flex flex-col mt-8 md:rounded md:bg-white md:py-6 md:px-10">
      <div className="flex justify-between align-start gap-4 px-4 md:px-0">
        <div>
          <H5 tag="h2" className="mb-3">
            {translate(i18nKey("latest_notifications_heading"))}
          </H5>
          <BodyRegular className="mb-6 text-black-5">
            {translate(i18nKey("latest_notifications_description"))}
          </BodyRegular>
        </div>
        <StrategyLatestNotificationsSettingsButton
          enabledNotificationTypesCount={enabledNotificationTypesCount}
          notificationTypes={dashboard.notificationTypes}
        />
      </div>
      {showEmptyStateLoader ? (
        <div className="flex justify-center py-6 md:py-8">
          <Loader small />
        </div>
      ) : notifications.length === 0 ? (
        renderEmptyNotificationsState()
      ) : (
        <ul className="mb-0 pl-0 list-none">
          {notifications.map((item, index) => (
            <li key={`sid-notification-${item.id}`} className="ml-0">
              <NotificationFactory
                markAsReadAction={markAsReadAction}
                notification_type={item.notification_type}
                message={item.message}
                sub_message={item.sub_message}
                config={item.config}
                date={item.date}
                read={item.read}
                item={item}
                isDesktop
              />
              {index < notifications.length - 1 ? (
                <div className="border-bottom border-bottom-gray-1 mx-4" />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {notifications.length !== 0 ? <StrategyViewAllNotificationsLink /> : null}
    </div>
  );
};

export default compose(
  withTranslations,
  withUserData,
  withNewNotifications,
  withAlertManagement
)(StrategyLatestNotifications);
